import { Grid } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { FormattedMessage, useIntl } from 'react-intl'
import Sequence from '../Sequence'
import { useUserSession } from '@utils/useUserSession'
import {
  useProductFromQueryParam,
  getProductSequenceInfoText,
  getAsteriskInsertionStatus,
  shouldRenderModifications,
  InsertModificationArgs,
} from '../../ProductsConfiguration'
import Asterisk from '../Asterisk'
import OligoName from '../OligoName'
import Modifications from '../Modifications/Modifications'
import SequencePreview from '../SequencePreview'
import OligoProperties from '../OligoProperties'
import SpecificationsHome from '../Specifications/SpecificationsHome'
import PageError from '../PageError'
import CsrOverride from '../CsrOverride'
import { TubeProduct } from '../../../../utils/StandardMTOEnum'
import ProbeType from './ProbeType'
import useFormikHelper from '../../useFormikHelper'
import SequenceEntryHeader from '../../SequencyEntryHeader'

interface DnaTubesProps {
  setLastCursorPosition: React.Dispatch<React.SetStateAction<number>>
  insertModificationIntoSequence: ({
    value,
    resetMod,
    isAntiSense,
  }: InsertModificationArgs) => Promise<string>
}
const useStyles = makeStyles((theme: Theme) => ({
  asterisk: {
    paddingTop: `${theme.spacing(0)}!important`,
    fontSize: theme.typography.pxToRem(12),
  },
  sequenceWithAsterisk: {
    paddingBottom: `${theme.typography.pxToRem(8)}!important`,
  },
  sequenceEntryTitleStyle: {
    borderBottom: `1px solid ${theme.palette.grey[500]}`,
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  requiredText: {
    fontSize: theme.typography.pxToRem(12),
  },
}))

const DnaTubes: React.FC<DnaTubesProps> = ({
  setLastCursorPosition,
  insertModificationIntoSequence,
}) => {
  const intel = useIntl()
  const classes = useStyles()
  const { userSession } = useUserSession()

  const formik = useFormikHelper()
  const product = useProductFromQueryParam()
  const asteriskEnabled = getAsteriskInsertionStatus(product)
  const renderModifications = shouldRenderModifications(product)
  const sequenceInfoText = getProductSequenceInfoText(
    product,
    userSession.country,
    formik.selectedOligoValues?.probeType
  )
  return (
    <Grid container spacing={4} style={{ marginTop: '24px' }}>
      <Grid item xs={12}>
        <PageError />
      </Grid>

      <Grid item xs={12} container spacing={6}>
        {product === TubeProduct.Probe && (
          <Grid item xs={12}>
            <ProbeType />
          </Grid>
        )}
      </Grid>
      <Grid item xs={12} container spacing={4}>
        <SequenceEntryHeader
          title={
            <FormattedMessage
              id="CONF_SEQUENCE_ENTRY"
              defaultMessage="Sequence Entry"
            />
          }
        />
        <Grid item xs={12}>
          <OligoName />
        </Grid>
        <Grid
          item
          xs={12}
          className={asteriskEnabled ? classes.sequenceWithAsterisk : undefined}
        >
          <Sequence
            label={intel.formatMessage({
              id: 'SEQUENCE_UPDATED',
              defaultMessage: "Sequence (5' to 3')",
            })}
            infoText={sequenceInfoText}
            setLastCursorPosition={setLastCursorPosition}
          />
        </Grid>
      </Grid>
      {asteriskEnabled && (
        <Grid item xs={12} className={classes.asterisk}>
          <Asterisk />
        </Grid>
      )}
      {renderModifications && (
        <Grid item xs={12}>
          <Modifications
            insertModificationIntoSequence={insertModificationIntoSequence}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <SequencePreview />
      </Grid>
      <Grid item xs={12}>
        <OligoProperties />
      </Grid>
      <Grid item xs={12}>
        <CsrOverride />
      </Grid>
      <Grid item xs={12}>
        <SpecificationsHome />
      </Grid>
    </Grid>
  )
}

export default DnaTubes
